import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CssBaseline, ThemeProvider } from '@mui/material'
import Grid from '@mui/system/Grid'

import { selectAppBarState, setAppBarState } from '@/app/slices/appSlice'
import { CuttingTechSelectionModal } from '@/common/components'
import { MainAppBar } from '@/common/components/MainAppBar'
import AppBarTargetContext from '@/common/contexts/AppBarTargetContext'
import { useCuttingTechModal, useFaviconUpdater, useToolBoxTreatments } from '@/common/hooks'
import { useAuth } from '@/common/hooks/useAuth'
import { theme as lightTheme } from '@/common/themes/LightTheme'
import { StrikerThemeOverride } from '@/common/themes/overrides/StrikerThemeOverride'
import { WhitelabelThemeOverride } from '@/common/themes/overrides/WhitelabelThemeOverride'

const classes = {
    contentContainer: {
        flexGrow: 1,
        backgroundColor: 'background.default',
        alignItems: 'flex-start',
        alignContent: 'flex-start',
        padding: 3,
        '@media print': {
            margin: 0,
            padding: 0,
            paddingTop: 1.5,
        },
    },
    footer: {
        textAlign: 'center',
        paddingBottom: 2,
        flexGrow: 0,
        '@media print': {
            display: 'none',
        },
    },
}

const Layout = ({ children }) => {
    const { isAuthenticated: userIsAuthenticated } = useAuth()
    const dispatch = useDispatch()

    const { showLibellulaWhitelabelling, showStrikerWhitelabelling } = useToolBoxTreatments()
    const { shouldShowModal } = useCuttingTechModal()

    // Update favicon and title based on product branding
    useFaviconUpdater()

    const appBarState = useSelector(selectAppBarState)

    const [appBarTarget, setAppBarTarget] = useState(null)
    const appBarContextValue = useMemo(() => ({ target: appBarTarget, setTarget: setAppBarTarget }), [appBarTarget])

    const theme = useMemo(
        () =>
            showLibellulaWhitelabelling
                ? WhitelabelThemeOverride(lightTheme)
                : showStrikerWhitelabelling
                  ? StrikerThemeOverride(lightTheme)
                  : lightTheme,
        [showLibellulaWhitelabelling, showStrikerWhitelabelling]
    )

    useEffect(() => {
        if (!userIsAuthenticated) {
            dispatch(
                setAppBarState({
                    menuHidden: true,
                    hidden: true,
                    myAccountHidden: true,
                    showUpdatePaymentBar: false,
                    showSubscriptionBar: false,
                    showTrialWithSubscriptionBar: false,
                })
            )
        }
    }, [userIsAuthenticated, dispatch])

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AppBarTargetContext.Provider value={appBarContextValue}>
                {!appBarState?.hidden ? <MainAppBar /> : null}

                <Grid
                    component="main"
                    flexGrow={1}
                    justifyContent={'center'}
                    sx={classes.contentContainer}
                    container
                >
                    {children}
                </Grid>

                {/* Cutting Technology Selection Modal - App-wide modal for subscription enforcement */}
                <CuttingTechSelectionModal
                    open={shouldShowModal}
                    onClose={() => {}}
                />
            </AppBarTargetContext.Provider>
        </ThemeProvider>
    )
}

export default Layout
